/**
 * useCamera — vehicle silhouette SVGs for the 'vehicle' guide mode
 *
 * These are rendered as semi-transparent white outlines inside the guide frame
 * to help the user frame the vehicle correctly. They are guidance-only — the
 * capture is not blocked if the vehicle doesn't match the silhouette.
 *
 * The fleet is mostly panel vans, so the exterior guides use a tall, boxy
 * panel-van shape rather than a low sports-car profile.
 */

import React from 'react';

/** Front view — panel van (tall, boxy cab-over face) */
export function VehicleSilhouetteFront() {
  return (
    <svg viewBox="0 0 200 140" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth={1.5}>
      {/* Body — high roof, near-vertical front face */}
      <path
        d="M 42 118 L 42 52 Q 42 30 62 28 L 138 28 Q 158 30 158 52 L 158 118 Q 158 122 153 122 L 47 122 Q 42 122 42 118 Z"
        strokeLinejoin="round"
      />
      {/* Windscreen — wide, set high on the face */}
      <path d="M 54 44 Q 100 38 146 44 L 142 66 Q 100 60 58 66 Z" strokeLinejoin="round" opacity={0.6} />
      {/* Grille */}
      <rect x={76} y={88} width={48} height={9} rx={2} opacity={0.5} />
      {/* Headlights */}
      <rect x={50} y={80} width={18} height={9} rx={3} opacity={0.5} />
      <rect x={132} y={80} width={18} height={9} rx={3} opacity={0.5} />
      {/* License plate */}
      <rect x={82} y={104} width={36} height={10} rx={2} opacity={0.7} />
      {/* Wheels hint */}
      <line x1={60} y1={122} x2={60} y2={130} opacity={0.4} />
      <line x1={140} y1={122} x2={140} y2={130} opacity={0.4} />
    </svg>
  );
}

/** Rear view — panel van (flat back with barn doors) */
export function VehicleSilhouetteRear() {
  return (
    <svg viewBox="0 0 200 140" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth={1.5}>
      {/* Body — flat, squared-off cargo back */}
      <path
        d="M 42 118 L 42 44 Q 42 28 60 27 L 140 27 Q 158 28 158 44 L 158 118 Q 158 122 153 122 L 47 122 Q 42 122 42 118 Z"
        strokeLinejoin="round"
      />
      {/* Barn-door split down the middle */}
      <line x1={100} y1={32} x2={100} y2={112} opacity={0.5} />
      {/* Door handles */}
      <rect x={88} y={70} width={9} height={4} rx={1.5} opacity={0.5} />
      <rect x={103} y={70} width={9} height={4} rx={1.5} opacity={0.5} />
      {/* Taillights — vertical strips at the corners */}
      <rect x={48} y={56} width={8} height={28} rx={2} opacity={0.5} />
      <rect x={144} y={56} width={8} height={28} rx={2} opacity={0.5} />
      {/* License plate */}
      <rect x={82} y={98} width={36} height={10} rx={2} opacity={0.7} />
      {/* Bumper line */}
      <line x1={50} y1={112} x2={150} y2={112} opacity={0.4} />
      {/* Wheels hint */}
      <line x1={60} y1={122} x2={60} y2={130} opacity={0.4} />
      <line x1={140} y1={122} x2={140} y2={130} opacity={0.4} />
    </svg>
  );
}

/** Side profile — panel van (high flat roof, boxy cargo hold, short bonnet).
 *  Front faces left by default (the view when standing on the vehicle's left
 *  side); `mirrored` flips it for the right-side photo. */
export function VehicleSilhouetteSide({ mirrored = false }: { mirrored?: boolean }) {
  return (
    <svg
      viewBox="0 0 240 120"
      className="w-full h-full"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      style={mirrored ? { transform: 'scaleX(-1)' } : undefined}
    >
      {/* Body — high flat roof running the full cargo length */}
      <path
        d="M 14 88 L 14 72 Q 14 64 22 62 L 38 58 Q 46 40 64 38 L 208 38 Q 226 38 226 52 L 226 82 Q 226 88 220 88 L 206 88 L 20 88 Q 14 88 14 88 Z"
        strokeLinejoin="round"
      />
      {/* Cab window */}
      <path d="M 52 56 Q 57 45 70 43 L 92 43 L 92 56 Z" strokeLinejoin="round" opacity={0.6} />
      {/* Cargo door seam */}
      <line x1={104} y1={42} x2={104} y2={84} opacity={0.4} />
      {/* Wheels */}
      <circle cx={62} cy={88} r={13} opacity={0.7} />
      <circle cx={62} cy={88} r={6} opacity={0.4} />
      <circle cx={192} cy={88} r={13} opacity={0.7} />
      <circle cx={192} cy={88} r={6} opacity={0.4} />
    </svg>
  );
}

/** Interior — steering wheel outline */
export function VehicleSilhouetteInterior() {
  return (
    <svg viewBox="0 0 200 160" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth={1.5}>
      {/* Steering wheel outer ring */}
      <circle cx={100} cy={80} r={55} opacity={0.7} />
      {/* Hub */}
      <circle cx={100} cy={80} r={18} opacity={0.5} />
      {/* Spokes */}
      <line x1={100} y1={62} x2={100} y2={25} opacity={0.5} />
      <line x1={82} y1={90} x2={45} y2={115} opacity={0.5} />
      <line x1={118} y1={90} x2={155} y2={115} opacity={0.5} />
      {/* Dashboard hint */}
      <path d="M 20 140 Q 100 130 180 140" opacity={0.4} strokeLinejoin="round" />
    </svg>
  );
}

/** Dashboard — instrument cluster outline */
export function VehicleSilhouetteDashboard() {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth={1.5}>
      {/* Dashboard top edge */}
      <path d="M 10 30 Q 100 20 190 30 L 190 85 Q 100 95 10 85 Z" strokeLinejoin="round" opacity={0.5} />
      {/* Speedometer */}
      <circle cx={70} cy={55} r={25} opacity={0.7} />
      {/* Tachometer */}
      <circle cx={130} cy={55} r={20} opacity={0.7} />
      {/* Center display */}
      <rect x={95} y={45} width={10} height={20} rx={2} opacity={0.5} />
    </svg>
  );
}

/** Renders the appropriate vehicle silhouette based on the profile */
export function VehicleSilhouette({ profile }: { profile: 'front' | 'rear' | 'side' | 'left_side' | 'right_side' | 'interior' | 'dashboard' }) {
  if (profile === 'rear') return <VehicleSilhouetteRear />;
  // The van outline faces left by default — correct for the vehicle's left
  // side (its front points left in frame). From the right side the front
  // points right, so the overlay must be mirrored.
  if (profile === 'right_side') return <VehicleSilhouetteSide mirrored />;
  if (profile === 'side' || profile === 'left_side') return <VehicleSilhouetteSide />;
  if (profile === 'interior') return <VehicleSilhouetteInterior />;
  if (profile === 'dashboard') return <VehicleSilhouetteDashboard />;
  return <VehicleSilhouetteFront />;
}
